/**
 * Form Validation Component
 * Handles validation logic for all field types
 */

'use strict';

var twentyninetimesCustomForm = twentyninetimesCustomForm || {};

twentyninetimesCustomForm.FormValidator = {
    /**
     * Validate all fields in a container
     * Required fields must be valid, non-required fields are validated if they have a value
     * @param {HTMLElement} container
     * @returns {boolean} true if all required fields are valid
     */
    validateContainer: function(container) {
        // Select all fields (required and non-required)
        const allFields = Array.from(container.querySelectorAll('.customform-field'));
        let allRequiredValid = true;
        let firstErrorField = null;

        allFields.forEach(fieldElm => {
            if (fieldElm.classList.contains('customform-conditionally-hidden')) {
                return;
            }

            const fieldType = fieldElm.getAttribute('data-field-type');
            const isRequired = fieldElm.classList.contains('customform-field-required');
            
            // For checkboxes/radios, validate the field itself; for others, validate the input inside
            const fieldValidate = (fieldType === 'checkbox' || fieldType === 'radio')
                ? fieldElm
                : fieldElm.querySelector('.customform-field-validate');
            
            // Only validate if required, or if not required but has a value
            const hasValue = fieldValidate && fieldValidate.value && fieldValidate.value.trim().length > 0;
            
            if (isRequired) {
                if (!this.validateField(fieldValidate, fieldType, true)) {
                    allRequiredValid = false;
                    if (!firstErrorField) firstErrorField = fieldValidate;
                }
            } else if (hasValue) {
                // Validate non-required fields only if they have a value
                if (!this.validateField(fieldValidate, fieldType, false)) {
                    if (!firstErrorField) firstErrorField = fieldValidate;
                }
            } else {
                // Clear error state for empty non-required fields
                if (fieldValidate) {
                    fieldValidate.classList.remove('customform-error');
                    this.toggleErrorLabel(fieldValidate, true, '');
                }
            }
        });

        // Scroll to first error field if any
        if (firstErrorField && twentyninetimesCustomForm.ScrollUtils) {
            twentyninetimesCustomForm.ScrollUtils.scrollToElement(firstErrorField);
        }

        return allRequiredValid;
    },

    /**
     * Validate a single field
     * @param {HTMLElement} field
     * @param {string} fieldType
     * @param {boolean} isRequired - true if field is required
     * @returns {boolean} true if valid
     */
    validateField: function(field, fieldType, isRequired) {
        if (!field) return false;
        
        let valid = false;
        let value = field.value || '';
        
        // Use error labels from customform_params.labels
        const labels = (window.customform_params && window.customform_params.labels) || {};
        let errorLabel = '';

        // Checkbox/Radio: at least one must be checked if required, otherwise skip if empty
        if (fieldType === 'checkbox' || fieldType === 'radio') {
            const inputs = Array.from(field.querySelectorAll('input[type="checkbox"],input[type="radio"]'));
            valid = inputs.some(input => input.checked);
            if (!valid && isRequired) errorLabel = labels.select || 'Please select an option.';
            else valid = !isRequired ? true : valid;
        }
        // Email: must match format and not be in blacklist
        else if (fieldType === 'email') {
            const mailformat = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
            const blacklist = field.getAttribute('data-email-domain-blacklist') || '';
            const emailDomains = blacklist.split(',').map(s => s.trim()).filter(Boolean);
            const userDomain = value.split('@').pop();
            
            if (value) {
                if (mailformat.test(value)) {
                    if (blacklist && emailDomains.includes(userDomain)) {
                        errorLabel = labels.business_email || 'Business email domains only.';
                        valid = false;
                    } else {
                        valid = true;
                    }
                } else {
                    errorLabel = labels.email || 'Invalid email address.';
                    valid = false;
                }
            } else {
                valid = !isRequired; // valid if not required and empty
                if (isRequired) errorLabel = labels.required || 'Email is required.';
            }
        }
        // URL: must match URL format
        else if (fieldType === 'url') {
            // Simple URL regex, can be improved for stricter validation
            const urlformat = /^(https?:\/\/)?([\w\d\-]+\.)+[\w\d\-]+(\/[^\s]*)?$/i;
            if (value) {
                if (urlformat.test(value)) {
                    valid = true;
                } else {
                    errorLabel = labels.url || 'Please enter a valid URL.';
                    valid = false;
                }
            } else {
                valid = !isRequired;
                if (isRequired) errorLabel = labels.required || 'URL is required.';
            }
        }
        // Telephone: must match phone regex
        else if (fieldType === 'tel') {
            const phoneno = /^\+?(\d{2} ?){4,5}/;
            if (value) {
                if (phoneno.test(value)) {
                    valid = true;
                } else {
                    errorLabel = labels.tel || 'Invalid phone number.';
                    valid = false;
                }
            } else {
                valid = !isRequired;
                if (isRequired) errorLabel = labels.required || 'Phone number is required.';
            }
        }
        // Number: rely on the browser's numeric, minimum, maximum, and step constraints
        else if (fieldType === 'number') {
            if (value) {
                valid = field.validity ? field.validity.valid : !isNaN(Number(value));
                if (!valid) errorLabel = labels.number || 'Please enter a valid number.';
            } else {
                valid = !isRequired;
                if (isRequired) errorLabel = labels.required || 'A number is required.';
            }
        }
        // Text: must be empty or > 1 character. If has value but only 1 character, always invalid.
        else {
            // Check for data-text-fullname attribute
            const requireFullName = field.hasAttribute('data-text-fullname');
            if (requireFullName && value) {
                // Must contain at least two words
                const wordCount = value.trim().split(/\s+/).length;
                if (wordCount < 2) {
                    valid = false;
                    errorLabel = labels.name || 'Please enter both first and last name.';
                } else if (value.length > 1) {
                    valid = true;
                } else {
                    valid = false;
                    errorLabel = labels.minlength || 'Field must be more than one character.';
                }
            } else if (value && value.length > 1) {
                valid = true;
            } else if (value && value.length === 1) {
                valid = false;
                errorLabel = labels.minlength || 'Field must be more than one character.';
            } else {
                valid = !isRequired;
                if (isRequired) errorLabel = labels.required || 'This field is required.';
            }
        }

        // Toggle error class and label
        field.classList.toggle('customform-error', !valid);
        this.toggleErrorLabel(field, valid, errorLabel);
        return valid;
    },

    /**
     * Show or hide error label for a field
     * @param {HTMLElement} field
     * @param {boolean} valid
     * @param {string} errorLabel
     */
    toggleErrorLabel: function(field, valid, errorLabel) {
        const fieldElm = field.closest('.customform-field');
        let errorElm = fieldElm.querySelector('label.customform-error');
        
        if (valid) {
            if (errorElm) fieldElm.removeChild(errorElm);
        } else {
            if (!errorElm) {
                errorElm = document.createElement('label');
                errorElm.classList.add('customform-error');
                errorElm.setAttribute('role', 'alert'); // Accessibility
                errorElm.innerText = errorLabel;
                fieldElm.appendChild(errorElm);
            } else {
                errorElm.innerText = errorLabel;
            }
        }
    }
};
